import { fetchSlimProfiles } from "@/api/hooks/profile";
import { Button } from "@/components/atoms/Button/Button";
import { Pill } from "@/components/atoms/Pill/Pill";
import { TooltipWrapper } from "@/components/molecules/TooltipWrapper/TooltipWrapper";
import { useAiChat } from "@/hooks/useAiChat";
import { useVoiceRecorder } from "@/hooks/useVoiceRecorder";
import { useAppDispatch } from "@/reducer";
import type { RootState } from "@/reducer";
import { PROFILE_FETCH_STATUS, useProfiles } from "@/reducer/ProfileState";
import { setAiChatRecording } from "@/reducer/UIState";
import type { AiChatMessage } from "@/types/ai-chat";
import { t } from "@lingui/core/macro";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { ActionCreators } from "redux-undo";
import { Window, type WindowIdentityProps, type WindowKind, closeAction, useDesktop } from "../window";
import { AiChatMessageRow, extractWorkspaceLink } from "./AiChatMessageRow";
import "./style.scss";
import { useWorkspaceAiContext } from "./useWorkspaceAIContext";

// Persist missing-profile requests across remounts to avoid repeated refetch loops when the window re-mounts.
const requestedMissingProfilesGlobal = new Set<number>();

export function AiChatWindow(props: WindowIdentityProps<WindowKind.AiChatWindow>) {
  const desktop = useDesktop();
  const conversationId: string | null = null;
  const scrollRef = useRef<HTMLDivElement>(null);
  // Use a shared set so it survives component remounts caused by window re-creation.
  const requestedMissingProfilesRef = useRef<Set<number>>(requestedMissingProfilesGlobal);
  const workspaceContext = useWorkspaceAiContext();
  const dispatch = useAppDispatch();
  const { profiles, fetchStatus } = useProfiles();
  const [revertStates, setRevertStates] = useState<Record<string, "idle" | "reverting" | "done" | "error">>({});
  const pastLength = useSelector((state: RootState) => state.tabGroup.past.length);
  const {
    messages,
    input,
    setInput,
    setMessages,
    isStreaming,
    error,
    canSend,
    send,
    sendAudio,
    reportError,
    handleCancel,
    resetConversation,
  } = useAiChat({
    conversationId,
    contextMessage: workspaceContext?.message ?? null,
    metadata: workspaceContext?.metadata ?? null,
    tabSnapshot: workspaceContext?.tabSnapshot ?? null,
  });
  const weatherSuggestion = t`What’s the weather situation for Zürich Airport this weekend?`;
  const metxSuggestion = t`How do I add a precipitation layer to my map?`;
  const placeholder = t`Chat with Meteomatics MetX Assistant…`;
  const {
    isRecording,
    start: startRecording,
    stop: stopRecording,
    abort: abortRecording,
  } = useVoiceRecorder({
    disabled: isStreaming,
    onAudio: (audio) => {
      void sendAudio(audio);
    },
    onError: reportError,
    onRecordingChange: (recording) => dispatch(setAiChatRecording(recording)),
  });

  const missingProfileIds = useMemo(() => {
    const ids = new Set<number>();
    for (const message of messages) {
      if (message.role !== "assistant") {
        continue;
      }
      const createdProfileId = message.meta?.createdDashboard?.profileId;
      if (typeof createdProfileId === "number" && !profiles[createdProfileId]) {
        ids.add(createdProfileId);
      }
      const { link } = extractWorkspaceLink(message.content);
      if (link && !profiles[link.profileId]) {
        ids.add(link.profileId);
      }
    }
    return Array.from(ids);
  }, [messages, profiles]);

  useEffect(() => {
    if (missingProfileIds.length === 0) {
      return;
    }
    if (fetchStatus === PROFILE_FETCH_STATUS.loading) {
      return;
    }
    const newIds = missingProfileIds.filter((id) => !requestedMissingProfilesRef.current.has(id));
    if (newIds.length === 0) {
      return;
    }
    for (const id of newIds) {
      requestedMissingProfilesRef.current.add(id);
    }
    dispatch(fetchSlimProfiles());
  }, [missingProfileIds, fetchStatus, dispatch]);

  useEffect(() => {
    if (scrollRef.current) {
      if (messages.length === 0 && !isStreaming) {
        return;
      }
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages, isStreaming]);

  useEffect(() => {
    return () => {
      abortRecording();
    };
  }, [abortRecording]);

  const handleRevert = useCallback(
    (messageId: string, undoIndex: number) => {
      const currentState = revertStates[messageId];
      if (currentState && currentState !== "idle") {
        return;
      }
      setRevertStates((prev) => ({ ...prev, [messageId]: "reverting" }));
      setMessages((prev: AiChatMessage[]) =>
        prev.map((msg: AiChatMessage) =>
          msg.id === messageId ? { ...msg, meta: { ...msg.meta, revert_status: "reverting" } } : msg,
        ),
      );
      try {
        dispatch(ActionCreators.jumpToPast(undoIndex));
        setRevertStates((prev) => ({ ...prev, [messageId]: "done" }));
        setMessages((prev: AiChatMessage[]) =>
          prev.map((msg: AiChatMessage) =>
            msg.id === messageId ? { ...msg, meta: { ...msg.meta, revert_status: "done" } } : msg,
          ),
        );
      } catch (err) {
        console.error("Revert failed", err);
        setRevertStates((prev) => ({ ...prev, [messageId]: "error" }));
        setMessages((prev: AiChatMessage[]) =>
          prev.map((msg: AiChatMessage) =>
            msg.id === messageId ? { ...msg, meta: { ...msg.meta, revert_status: "error" } } : msg,
          ),
        );
      }
    },
    [revertStates, dispatch, setMessages],
  );

  function closeWindow() {
    desktop.dispatch({
      type: "setWindowVisibility",
      windowId: props.instanceId,
      isVisible: false,
    });
  }
  const actions = [closeAction(() => closeWindow())];

  const handleSubmit = useCallback(
    (e: React.FormEvent) => {
      e.preventDefault();
      if (!canSend) {
        return;
      }

      const text = input;
      setInput("");
      void send(text);
    },
    [canSend, input, send, setInput],
  );
  const handleInputKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
      if (e.key === "Enter" && !e.shiftKey) {
        e.preventDefault();
        if (!canSend) {
          return;
        }
        const text = input;
        setInput("");
        void send(text);
      }
    },
    [canSend, input, send, setInput],
  );
  const inputRows = useMemo(() => {
    const linesCount = input.split("\n").length;
    return Math.min(5, Math.max(1, linesCount));
  }, [input]);

  const handleRecordPointerDown = useCallback(
    (e: React.PointerEvent<HTMLButtonElement>) => {
      e.preventDefault();
      void startRecording();
    },
    [startRecording],
  );

  const handleRecordPointerUp = useCallback(
    (e: React.PointerEvent<HTMLButtonElement>) => {
      e.preventDefault();
      stopRecording();
    },
    [stopRecording],
  );

  const handleRecordPointerLeave = useCallback(() => {
    if (isRecording) {
      stopRecording();
    }
  }, [isRecording, stopRecording]);

  const newChatButton = (
    <TooltipWrapper tooltipText={t`New chat`} position="top" tail>
      <Button
        type="button"
        buttonType="secondary"
        className={"icon-button header-button"}
        centerIconName="edit_square"
        onClick={resetConversation}
        disabled={isStreaming}
      />
    </TooltipWrapper>
  );

  return (
    <Window
      kind={"ai-chat"}
      title={t`MetX Assistant`}
      {...props}
      leftButton={newChatButton}
      actions={actions}
      className={"ai-chat-window"}
      scrollable={false}
    >
      <div className={"divider"} />

      <div className={"beta-message-container"}>
        <Pill className={"beta-pill"}>BETA</Pill>
        <div className={"beta-message"}>{t`AI outputs may be inaccurate. Please verify the results`}</div>
      </div>

      <div className={"chat-content-container"} ref={scrollRef}>
        {messages.map((message) => {
          const revertState = message.meta?.revert_status ?? revertStates[message.id] ?? "idle";
          const revertUndoIndex = message.meta?.revertUndoIndex;
          const canRevert =
            message.role === "assistant" &&
            typeof revertUndoIndex === "number" &&
            Number.isFinite(revertUndoIndex) &&
            pastLength > revertUndoIndex;
          return (
            <AiChatMessageRow
              key={message.id}
              message={message}
              onRevert={handleRevert}
              revertState={revertState}
              revertUndoIndex={revertUndoIndex}
              canRevert={canRevert}
            />
          );
        })}
        {isStreaming && <div className={"is-typing-message"}>{t`Assistant is typing…`}</div>}
        {error && <div className={"error-message"}>{error}</div>}
      </div>

      {messages.length <= 1 && (
        <div className={"suggestions-container"}>
          {/* biome-ignore lint/a11y/useKeyWithClickEvents: <explanation> */}
          <div className={"suggestion-item"} onClick={() => send(weatherSuggestion)}>
            {weatherSuggestion}
          </div>
          {/* biome-ignore lint/a11y/useKeyWithClickEvents: <explanation> */}
          <div className={"suggestion-item"} onClick={() => send(metxSuggestion)}>
            {metxSuggestion}
          </div>
        </div>
      )}

      <form className={"chat-input-container"} onSubmit={handleSubmit}>
        <div className="chat-input-shell">
          <textarea
            value={input}
            onChange={(e) => setInput(e.currentTarget.value)}
            onKeyDown={handleInputKeyDown}
            placeholder={placeholder}
            disabled={isStreaming || isRecording}
            aria-label="Chat input"
            className={"chat-input"}
            rows={inputRows}
          />
          <TooltipWrapper tooltipText={isRecording ? t`Release to send` : t`Hold to record`} position="top" tail={true}>
            <Button
              type="button"
              buttonType="clear"
              className={`icon-button voice-button ${isRecording ? "is-recording" : ""}`}
              centerIconName="mic"
              onPointerDown={handleRecordPointerDown}
              onPointerUp={handleRecordPointerUp}
              onPointerLeave={handleRecordPointerLeave}
              onPointerCancel={handleRecordPointerLeave}
              disabled={isStreaming}
              aria-label={isRecording ? t`Stop recording` : t`Hold to record`}
            />
          </TooltipWrapper>
        </div>
        <div className="submit-button-container">
          {isStreaming ? (
            <Button
              type="button"
              className={"secondary icon-button send-button"}
              centerIconName="stop"
              iconStyle="filled"
              onClick={handleCancel}
            >
              Stop
            </Button>
          ) : (
            <Button
              type="submit"
              className={"primary icon-button send-button"}
              centerIconName="keyboard_return"
              disabled={!canSend || isRecording}
            />
          )}
        </div>
      </form>
    </Window>
  );
}
